import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import Button from '../Button/Button';
import FormulaireAdministratif from '../FormulaireAdministratif/FormulaireAdministratif';
import CreationArticle from '../CreationArticle/CreationArticle';
import CGVAccept from '../CGVAccept/CGVAccept';
import ValidationPopUp from '../ValidationPopUp/ValidationPopUp';
import { pdf } from '@react-pdf/renderer';
import ConsultationPDF from '../../pages/PDF/ConsultationPDF';
import { useAuth } from '../../contexts/AuthContext';

import './Consultation.css'

interface ConsultationProps {
  commande: any;
  errors: {
    besoin?: string;
    nomOperation?: string;
    adresse?: string;
    codePostal?: string;
    ville?: string;
    conditionVisite?: string;
    referenceInterne?: string;
  };
  setErrors: React.Dispatch<React.SetStateAction<{
    besoin?: string;
    nomOperation?: string;
    adresse?: string;
    codePostal?: string;
    ville?: string;
    conditionVisite?: string;
    referenceInterne?: string;
  }>>;
  setCommande: React.Dispatch<React.SetStateAction<any>>;
  acceptCGV: boolean;
  setAcceptCGV: React.Dispatch<React.SetStateAction<boolean>>;
  handleExport: () => void;
  validateConsultationForm: () => boolean;
  handleLancerConsultation: () => Promise<void>;
  viewOnly?: boolean;
  hasFormData?: boolean; // Nouvelle propriété pour indiquer si les données du formulaire sont disponibles
}

const Consultation: React.FC<ConsultationProps> = ({
  commande,
  errors,
  setErrors,
  setCommande,
  acceptCGV,
  setAcceptCGV,
  handleExport,
  validateConsultationForm,
  handleLancerConsultation,
  viewOnly = false,
  hasFormData = true // Par défaut, on suppose que les données sont disponibles
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Vérifier si les données de formulaire sont présentes et correctes
  const isFormDataValid = React.useMemo(() => {
    if (!commande || !commande.formulaire) return false;

    // Vérifier si les propriétés essentielles existent
    const hasBasicInfo = commande.formulaire.nomOperation ||
      commande.formulaire.adresse ||
      commande.formulaire.besoin;

    // Vérifier si les sections existent et sont un tableau
    const hasSections = Array.isArray(commande.formulaire.sections) &&
      commande.formulaire.sections.length > 0;

    return hasBasicInfo || hasSections;
  }, [commande]);

  // Si les données ne sont pas disponibles, afficher un message
  if (!hasFormData || !isFormDataValid) {
    return (
      <div className="tab-consultation">
        <div className="no-data-message" style={{
          padding: '30px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          margin: '20px 0'
        }}>
          <h3 style={{ marginBottom: '15px', color: '#495057' }}>
            Informations non disponibles
          </h3>
          <p style={{ fontSize: '16px', lineHeight: '1.5', color: '#6c757d' }}>
            Les informations de consultation ne sont pas disponibles pour ce marché subséquent.
          </p>
          <p style={{ fontSize: '16px', lineHeight: '1.5', color: '#6c757d' }}>
            Ce marché est géré par un autre utilisateur ou est en cours de traitement.
          </p>
          <Button
            value="Retour aux opérations"
            variant="secondary"
            onClick={() => navigate('/operations')}
            size="medium"
            style={{ marginTop: '20px' }}
          />
        </div>
      </div>
    );
  }

  // Ajout d'une référence pour stocker l'état initial des sections/articles
  const initialSectionsRef = useRef<any>(null);

  // Initialiser la référence au premier rendu
  useEffect(() => {
    if (commande.formulaire?.sections && !initialSectionsRef.current) {
      initialSectionsRef.current = JSON.stringify(commande.formulaire.sections);
    }
  }, [commande.formulaire?.sections]);

  // Fonction utilitaire pour comparer les sections/articles
  const isModified = React.useMemo(() => {
    if (!initialSectionsRef.current) return false;
    const current = JSON.stringify(commande.formulaire?.sections || []);
    return current !== initialSectionsRef.current;
  }, [commande.formulaire?.sections]);

  // État pour gérer le modal de confirmation
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCGVModalOpen, setIsCGVModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [readyToLaunchAfterCGV, setReadyToLaunchAfterCGV] = useState(false); // Nouvel état

  // Fonction de validation des champs requis - version complète
  const validateRequiredFields = () => {
    const newErrors: {
      nomOperation?: string;
      adresse?: string;
      codePostal?: string;
      ville?: string;
      besoin?: string;
      conditionVisite?: string;
      referenceInterne?: string;
    } = {};

    // Validation des champs obligatoires du formulaire administratif
    if (!commande.formulaire?.nomOperation?.trim()) {
      newErrors.nomOperation = "Le nom de l'opération est obligatoire";
    }

    // if (!commande.formulaire?.referenceInterne?.trim()) {
    //   newErrors.referenceInterne = "Le numéro de référence interne est obligatoire";
    // }

    if (!commande.formulaire?.adresse?.trim()) {
      newErrors.adresse = "L'adresse est obligatoire";
    }

    if (!commande.formulaire?.codePostal?.trim()) {
      newErrors.codePostal = "Le code postal est obligatoire";
    }

    if (!commande.formulaire?.ville?.trim()) {
      newErrors.ville = "La ville est obligatoire";
    }

    // Validation de la définition du besoin (descriptif technique)
    if (!commande.formulaire?.besoin?.trim()) {
      newErrors.besoin = "Le cahier des charges est obligatoire";
    }

    // Si une visite est prévue, valider les conditions
    if (commande.formulaire?.visitePreavis && !commande.formulaire?.conditionVisite?.trim()) {
      newErrors.conditionVisite = "Les conditions de visite sont obligatoires";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Fonction personnalisée pour générer le PDF
  const generatePDF = async () => {
    try {

      // Vérifier que les données requises sont présentes
      if (!commande || !user) {
        console.error('Données manquantes pour générer le PDF');
        alert('Impossible de générer le PDF : données manquantes');
        return;
      }

      // S'assurer que le besoin est défini pour éviter des erreurs
      if (!commande.formulaire) {
        commande.formulaire = {};
      }

      // Préparation des données pour le PDF
      const pdfData = {
        ...commande,
        formulaire: commande.formulaire || {}
      };
      console.log('Génération du PDF en cours...', pdfData);

      // Générer le PDF avec une gestion d'erreurs améliorée
      const blob = await pdf(<ConsultationPDF commande={pdfData} user={user} />).toBlob();

      // Télécharger le PDF
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `consultation-${commande.numero || commande.id || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Une erreur est survenue lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  // Fonction pour vérifier s'il y a au moins un article dans la commande
  const hasArticles = () => {
    // Vérifier si la commande a des sections avec des articles
    if (commande.formulaire?.sections && Array.isArray(commande.formulaire.sections)) {
      // Vérifier que chaque section contient au moins un article
      if (commande.formulaire.sections.length === 0) {
        return false; // Aucune section n'est disponible
      }

      // S'assurer que chaque section contient au moins un article
      const hasSectionsWithoutArticles = commande.formulaire.sections.some(section =>
        !section.articles || !Array.isArray(section.articles) || section.articles.length === 0
      );

      if (hasSectionsWithoutArticles) {
        return false; // Au moins une section est vide
      }

      return true; // Toutes les sections ont au moins un article
    }

    // Si on utilise l'ancienne structure avec un tableau d'articles directement
    if (commande.formulaire?.articles && Array.isArray(commande.formulaire.articles)) {
      return commande.formulaire.articles.length > 0;
    }

    // Si on cherche aussi dans la commande directement (fallback)
    if (commande.articles && Array.isArray(commande.articles)) {
      return commande.articles.length > 0;
    }

    return false;
  };

  // Fonction pour vérifier les champs et ouvrir le modal de confirmation
  const handleConfirmLaunch = () => {
    // Vérifier d'abord si les CGV sont acceptées
    if (!acceptCGV) {
      alert("Vous devez accepter les CG pour lancer la consultation.");
      return;
    }

    // Vérifier qu'au moins un article a été créé
    if (!hasArticles()) {
      alert("Vous devez créer au moins un article avant de lancer la consultation.");
      return;
    }

    if (validateConsultationForm()) {
      setIsConfirmModalOpen(true);
    } else {
      // Faire défiler jusqu'au premier champ d'erreur
      const errorElement = document.querySelector('.error');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Fonction pour sauvegarder les données - version améliorée
  const saveOperationData = async (validateRequired: boolean = false) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Aucun token d\'authentification trouvé');
      }

      // Validation conditionnelle
      if (validateRequired && !validateRequiredFields()) {
        throw new Error('Veuillez remplir tous les champs obligatoires');
      }

      // Récupérer l'ID de l'opération depuis l'URL
      const currentPath = window.location.pathname;
      const operationId = currentPath.split('/')[2];

      if (!operationId) {
        throw new Error('ID de l\'opération non trouvé');
      }

      // S'assurer que les dates sont bien formatées pour l'API
      const formatDateForApi = (dateStr) => {
        if (!dateStr || dateStr === '') return null;
        try {
          // S'assurer que la date est au format YYYY-MM-DD
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return null; // Date invalide
          return date.toISOString().split('T')[0];
        } catch (e) {
          console.warn("Erreur de formatage de date:", e);
          return null;
        }
      };

      const dataToSend = {
        nom: commande.formulaire?.nomOperation?.trim() || '',
        reference: commande.formulaire?.referenceInterne?.trim() || '',
        besoin: commande.formulaire?.besoin?.trim() || '',
        delais: commande.formulaire?.delaisConsultation?.toString() || "30",
        visite: commande.formulaire?.visitePreavis ? "1" : "0",
        adresse: commande.formulaire?.adresse?.trim() || '',
        code_postal: commande.formulaire?.codePostal?.trim() || '',
        ville: commande.formulaire?.ville?.trim() || '',
        coordonnees_maitrise: commande.formulaire?.coordonneesMaitrise?.trim() || '',
        coordonnees_maitrise_ouvrage: commande.formulaire?.coordonneesMaitriseDOuvrage?.trim() || '',
        conditionVisite: commande.formulaire?.conditionVisite?.trim() || '',
        // Correction: utiliser la fonction de formatage pour les dates
        date_debut_operation: formatDateForApi(commande.formulaire?.dateDebutOperation),
        date_fin_operation: formatDateForApi(commande.formulaire?.dateFinOperation),
        delais_fin_operation: commande.formulaire?.delaisFinOperation?.toString() || '',
      };

      console.log('Sauvegarde des données:', dataToSend);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/subsequents/operation/${operationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Gérer les erreurs de validation de l'API
        if (response.status === 422 && errorData.errors) {
          const apiErrors: any = {};
          Object.keys(errorData.errors).forEach(field => {
            const messages = errorData.errors[field];
            if (Array.isArray(messages) && messages.length > 0) {
              // Mapper les noms de champs API vers les noms utilisés dans le frontend
              const fieldMapping: { [key: string]: string } = {
                'nom': 'nomOperation',
                'reference': 'referenceInterne',
                'besoin': 'besoin',
                'adresse': 'adresse',
                'code_postal': 'codePostal',
                'ville': 'ville',
                'conditionVisite': 'conditionVisite'
              };
              const frontendField = fieldMapping[field] || field;
              apiErrors[frontendField] = messages[0];
            }
          });
          setErrors(apiErrors);
          throw new Error('Erreurs de validation détectées');
        }

        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.status !== 'success') {
        throw new Error(responseData.message || 'Erreur lors de la sauvegarde');
      }

      return responseData;

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      throw error;
    }
  };

  // Fonction pour sauvegarder et quitter - utilise saveOperationData
  const handleSaveAndQuit = async () => {
    setIsSaving(true);

    try {
      await saveOperationData(true); // Valider les champs requis
      console.log('Sauvegarde réussie');
      navigate('/operations');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';

      // Si c'est une erreur de validation, faire défiler vers le premier champ d'erreur
      if (errorMessage === 'Erreurs de validation détectées') {
        setTimeout(() => {
          const errorElement = document.querySelector('.error');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Fonction pour sauvegarder automatiquement (sans validation stricte)
  const handleAutoSave = async () => {
    try {
      await saveOperationData(false); // Pas de validation stricte pour l'auto-save
    } catch (error) {
      console.warn('Sauvegarde automatique échouée:', error);
    }
  };

  // Vérifier si le bouton "Lancer la consultation" doit être désactivé
  const isLaunchButtonDisabled = !acceptCGV || !hasArticles();
  console.log('operation: ', commande);
  return (
    <div className="tab-consultation">
      {/* Première partie : Descriptif Administratif */}
      <section>
        <h3 className='section-header'>Descriptif Administratif</h3>
        <div className="section-content">
          <FormulaireAdministratif
            formData={commande.formulaire || {}}
            viewMode={commande.status.nom !== 'Brouillon'}
            hideBesoin={true}
            operation={commande}
            errors={errors} // Passer les erreurs au formulaire
            setFormData={commande.status.nom === 'Brouillon' ? (newData) => {
              setCommande((prev: any) => {
                if (!prev) return prev;

                const isEqual = Object.keys(newData).every(key =>
                  JSON.stringify(prev.formulaire?.[key]) === JSON.stringify(newData[key])
                );

                if (isEqual) return prev;

                // Nettoyer les erreurs pour les champs modifiés
                const updatedErrors = { ...errors };
                Object.keys(newData).forEach(key => {
                  if (key in updatedErrors) {
                    delete (updatedErrors as any)[key];
                  }
                });
                setErrors(updatedErrors);

                // Supprimer l'auto-sauvegarde
                // setTimeout(handleAutoSave, 1000);

                return {
                  ...prev,
                  formulaire: { ...prev.formulaire, ...newData }
                };
              });
            } : undefined}
          />
        </div>
      </section>

      {/* Deuxième partie : Descriptif Technique */}
      <section>
        <h3 className='section-header'>Descriptif Technique</h3>
        <div className="section-content">
          {commande.status.nom === 'Brouillon' ? (
            <div className="form-group">
              <label>
                Cahier des charges <span style={{ color: 'red' }}>*</span>
              </label>
              <textarea
                value={commande.formulaire?.besoin || ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setCommande((prev) => {
                    if (!prev) return prev;
                    if (prev.formulaire?.besoin === newValue) return prev;

                    // Nettoyer l'erreur si l'utilisateur tape quelque chose
                    if (newValue.trim() && errors.besoin) {
                      setErrors((prevErrors) => ({ ...prevErrors, besoin: undefined }));
                    }

                    // Supprimer l'auto-sauvegarde
                    // setTimeout(handleAutoSave, 1000);

                    return {
                      ...prev,
                      formulaire: {
                        ...prev.formulaire,
                        besoin: newValue
                      }
                    };
                  });
                }}
                className={`definition-besoin-textarea ${errors.besoin ? 'error' : ''}`}
                placeholder="Cahier des charges (obligatoire)"
                rows={20}
              />
              {errors.besoin && (
                <span className="error-message">{errors.besoin}</span>
              )}
            </div>
          ) : (
            <div className="form-group">
              <label>Cahier des charges</label>
              <div className="input-inactif definition-besoin">
                {commande.formulaire?.besoin || '-'}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Troisième partie : Descriptif Financier */}
      <section>
        <h3 className='section-header'>Descriptif Financier</h3>
        <div className="section-content">
          <CreationArticle
            formData={commande.formulaire || {}}
            viewMode={commande.status.nom !== 'Brouillon'} // Éditable si en brouillon
            setFormData={commande.status.nom === 'Brouillon' ? (newData) => {
              setCommande(prev => ({
                ...prev!,
                formulaire: { ...prev!.formulaire, ...newData }
              }));
            } : undefined}
          />
        </div>
      </section>
      <div className="action-buttons">
        <Button
          value="PDF"
          icon={<PdfIcon />}
          variant="secondary"
          onClick={generatePDF}
          size="small"
        />
        {/* Boutons d'actions */}
        {commande.status.nom === 'Brouillon' && (
          <>

            {/* <Button
            value="Accepter les CG"

            variant="primary"
            size='small'
            onClick={() => setIsCGVModalOpen(true)}
          // className="cgv-view-button"
          ></Button> */}
            <CGVAccept
              acceptCGV={acceptCGV}
              setAcceptCGV={setAcceptCGV}
              isOpen={isCGVModalOpen}
              onClose={() => {
                setIsCGVModalOpen(false);
                // Réinitialiser l'état si l'utilisateur ferme sans accepter
                if (readyToLaunchAfterCGV) {
                  setReadyToLaunchAfterCGV(false);
                }
              }}
              onConfirm={() => {
                // Si l'utilisateur a accepté les CGV et était prêt à lancer la consultation
                if (acceptCGV && readyToLaunchAfterCGV) {
                  setReadyToLaunchAfterCGV(false);
                  // Afficher la pop-up de confirmation après un court délai
                  setTimeout(() => handleConfirmLaunch(), 100);
                }
              }}
            />
            <Button
              value={isSaving ? "Sauvegarde en cours..." : "Sauvegarder et quitter"}
              variant="secondary"
              onClick={handleSaveAndQuit}
              size="small"
              disabled={isSaving}
            />

            <Button
              value="Lancer la consultation"
              variant="primary"
              onClick={async () => {
                // Vérifier d'abord si tous les champs sont valides et s'il y a au moins un article
                if (validateRequiredFields() && hasArticles()) {
                  try {
                    // Sauvegarder les données avant de continuer
                    setIsSaving(true);
                    await saveOperationData(true);
                    setIsSaving(false);

                    // Après la sauvegarde, vérifier si les CGV sont acceptées
                    if (!acceptCGV) {
                      // Si CGV non acceptées, ouvrir le modal des CGV
                      setReadyToLaunchAfterCGV(true);
                      setIsCGVModalOpen(true);
                    } else {
                      // Si CGV déjà acceptées, afficher directement la confirmation
                      handleConfirmLaunch();
                    }
                  } catch (error) {
                    setIsSaving(false);
                    const errorMessage = error instanceof Error ? error.message : 'Une erreur est survenue lors de la sauvegarde.';

                    if (errorMessage === 'Erreurs de validation détectées') {
                      setTimeout(() => {
                        const errorElement = document.querySelector('.error');
                        if (errorElement) {
                          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                      }, 100);
                    } else {
                      alert('Erreur lors de la sauvegarde: ' + errorMessage);
                    }
                  }
                } else {
                  // Afficher les messages d'erreur appropriés
                  if (!validateRequiredFields()) {
                    alert('Veuillez remplir tous les champs obligatoires avant de lancer la consultation.');
                  } else if (!hasArticles()) {
                    alert('Vous devez créer au moins un article avant de lancer la consultation.');
                  }
                }
              }}
              size="small"
              title={
                !acceptCGV
                  ? "Vous devez accepter les CG"
                  : !hasArticles()
                    ? "Vous devez créer au moins un article"
                    : ""
              }
            />

          </>)}
      </div>
      {/* Modal de confirmation pour lancer la consultation */}
      <ValidationPopUp
        isOpen={isConfirmModalOpen}
        title={'Lancement de la consultation'}
        text={`A partir de votre confirmation, les titulaires du lot seront consultés et disposeront de ${commande.formulaire?.delaisConsultation} jours pour candidater. Il n'est plus possible de modifier la consultation après cette étape.`}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={async () => {
          setIsConfirmModalOpen(false);
          await handleLancerConsultation();

          // Récupérer l'URL actuelle et ajouter ?tab=3
          const currentPath = window.location.pathname;
          navigate(`${currentPath}?tab=2`);
        }}
        cancelLabel="Non"
        confirmLabel="Oui"
      />
    </div>
  );
};

export default Consultation;